import ColumnHeader from "@/components/data-table/ColumnHeader";
import DataTable from "@/components/data-table/DataTable";
import { DateConverter, formatAmount, STALE_TIME_INVOICE } from "@/constants";
import { Invoice } from "@/context/Invoice/InvoiceState";
import { UserContext } from "@/context/User/UserContext";
import { FinanceSecondaryListInvoices } from "@/services/invoice-functions";
import { ColumnDef } from "@tanstack/react-table";
import { useContext } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import InvoiceTag from "@/components/data-table/InvoiceTag";

const FinanceSecondaryApprovals = () => {
	const user = useContext(UserContext);
	const invoices = useQuery({
		queryKey: ["invoice", "finance", "secondary"],
		queryFn: () => FinanceSecondaryListInvoices(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: STALE_TIME_INVOICE,
	});

	const navigate = useNavigate();

	const handleRowClick = (id: number) => {
		navigate(`/financesecondaryapproval/${id}`);
	};

	return (
		<>
			{invoices.data ? (
				<DataTable
					columns={Columns}
					data={invoices.data}
					rowClick={handleRowClick}
					rowId="invoicemasterid"
					searchColumn="raisedby"
				/>
			) : null}
			{invoices.isLoading ? (
				<div className="flex justify-center">
					<span className="loader" />
				</div>
			) : invoices.isError ? (
				<div className="align-centre">
					<h5 className="text-center">Error showing claims.</h5>
				</div>
			) : (
				invoices.data &&
				invoices.data.length === 0 && (
					<div className="align-centre">
						<h5 className="text-center">No claims found</h5>
					</div>
				)
			)}
		</>
	);
};

export default FinanceSecondaryApprovals;

export const Columns: ColumnDef<Invoice>[] = [
	{
		accessorKey: "invoicemasterid",
		enableHiding: false,
		header: ({ column }) => <ColumnHeader column={column} title="Claim ID" />,
		size: 100,
		maxSize: 120,
	},
	{
		accessorKey: "description",
		header: ({ column }) => <ColumnHeader column={column} title="Description" />,
		cell: ({ row }) => {
			const invoice = row.original;
			return (
				<div className="text-sm flex gap-1 items-center">
					<InvoiceTag id={invoice.invoicetypeid} type={invoice.invoicetype} />
					<p>
						{invoice.description.length > 100
							? invoice.description.slice(0, 100) + "..."
							: invoice.description}
					</p>
				</div>
			);
		},
	},
	{
		accessorKey: "amount",
		enableHiding: false,
		header: ({ column }) => <ColumnHeader column={column} title="Amount" />,
		cell: ({ row }) => {
			const country = row.original.country;

			return (
				<div className="font-medium">
					{formatAmount(row.getValue("amount"), country ? "USD" : "Ksh")}
				</div>
			);
		},
	},
	{
		accessorKey: "raisedby",
		enableHiding: false,
		header: ({ column }) => <ColumnHeader column={column} title="Raised By" />,
	},
	{
		accessorKey: "raisedon",
		header: ({ column }) => <ColumnHeader column={column} title="Raised On" />,
		cell: ({ row }) => {
			const date = new Date(row.getValue("raisedon"));
			return DateConverter(date);
		},
	},
];
